<template>
  <div class="demo">
    <div id="glview" ref="glview"></div>
    <progress-ring
      id="progress"
      ref="prog"
      :percent="progressPct"
      :width="progressSize"
      :height="progressSize"
      :strokeWidth="4"
      :rgbaTrackColor="'rgba(0, 0, 0, 0.075)'"
      :rgbaRingColor="'rgba(0, 0, 0, 0.25)'"
      :alwaysShow="true"
      @onComplete="onProgressComplete" />

    <progress-ring
      id="stageProgress"
      ref="stageProg"
      :percent="stageProgressPct"
      :width="progressSize - 20"
      :height="progressSize - 20"
      :strokeWidth="4"
      :rgbaTrackColor="'rgba(0, 0, 0, 0.05)'"
      :rgbaRingColor="stageRingColor"
      :dashed="true"
      :segments="stages[currentStage].touchesToComplete"
      :segmentGap="50"
      :alwaysShow="true" />

    <div class="info d-flex flex-column justify-content-end">
      <div class="info-txt info0 adobe-clean-serif">
        <span>Neat, you're on {{ indefiniteModel }}...</span>
        <span class="green">{{ device[stages[0].info] }}</span>
      </div>
      <div class="info-txt info1 adobe-clean-serif">
        <span>It's beautiful in...</span>
        <span class="orange">{{ device['city'] }}</span>
      </div>
      <div class="info-txt info2 adobe-clean-serif">
        <span>{{ resultPreCopy }}</span>
        <span class="purple">{{ resultCopy }}</span>
      </div>
    </div>

    <div class="intro d-flex flex-column align-items-center justify-content-center" ref="intro" v-if="introActive">
      <div class="intro-item">
        <next-mobile-decade class="nmd" />
      </div>
      <scroll-icon class="icon intro-item" :complete="iconComplete" :active="iconActive" :isButton="true" />
      <h3 class="adobe-clean-serif intro-item text-center">Interact and react. Give us some time to learn about you.</h3>
    </div>

    <div class="hint d-flex align-items-center justify-content-center" ref="hint">
      <h5>HINT: <span>Drag, touch, play...</span></h5>
    </div>

    <div v-if="isComplete" class="selection">
      <div class="interstitial d-flex flex-column align-items-center justify-content-center">
        <scroll-icon class="icon select-item" :complete="iconComplete" :active="true" :isButton="true" />
        <h3 class="knockout-50 select-item">COMPLETED</h3>
        <p class="text-center adobe-clean-serif text-italic select-item">
          Good to know. Your report has been optimized for readability.
        </p>
      </div>

      <div v-if="outroActive"
        ref="outro"
        class="outro d-flex flex-column align-items-center user-select-none justify-content-center"
        @click="$router.push('terrain')">
          <div class="outro-item">
            <next-mobile-decade class="nmd" />
          </div>
          <p class="adobe-clean-serif text-center outro-item">
            Data is everywhere. How you use it is what decides the future.
          </p>
          <div class="cta d-flex flex-column justify-content-end outro-item">
            <span>Tap to continue</span>
          </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState } from 'vuex'
import { TimelineMax, TweenMax, Power2, Power3, Elastic } from 'TweenMax'
import { Math as ThreeMath } from 'three'
import THREE from '@/lib/three-extra'
import { displacementShader, starfieldShader } from '@/lib/shaders'
import { indefiniteArticle } from '@/lib/funcs'
import { reportTxtStyles } from '@/lib/consts'
import GLViewMixin from '@/mixins/GLViewMixin'
import Chevron from '@/components/svg/Chevron'
import ScrollIcon from '@/components/svg/ScrollIcon'
import NextMobileDecade from '@/components/svg/NextMobileDecade'
import ProgressRing from './ProgressRing'

export default {
  name: 'demo-scroll',
  components: {
    Chevron,
    ProgressRing,
    ScrollIcon,
    NextMobileDecade
  },
  mixins: [GLViewMixin],
  data () {
    return {
      skipIntro: false, // <-- set to false for prod
      idx: 2,
      fullWidth: 0,
      fullHeight: 0,
      introActive: true,
      introDelay: 3,
      outroActive: false,
      isComplete: false,
      shouldUpdate: true,
      delta: 0,
      maxStars: 3000,
      touchActive: false,
      lastTouch: {},
      tapThreshold: 70,
      didIncreaseByTime: false,
      touches: [],
      lights: {
        primary: undefined,
        secondary: undefined
      },
      stages: [
        {
          attributes: {
            displacement: { type: 'v3', value: [] },
            customColor: { type: 'c', value: [] }
          },
          uniforms: {
            amplitude: { value: 0.0 },
            opacity: { value: 1.0 },
            transparent: true
          },
          colors: {
            secondary: 0x2FDBB7,
            primary: 0xC0FFBA
          },
          lights: {
            primary: 1.0,
            secondary: 0.5
          },
          numTouches: 0,
          touchScreenTime: 0,
          touchesToComplete: 3,
          timeToIncrease: 800,
          percent: 0.333,
          complete: false,
          info: 'model'
        },
        {
          attributes: {
            displacement: { type: 'v3', value: [] },
            customColor: { type: 'c', value: [] }
          },
          uniforms: {
            amplitude: { value: 0.0 },
            opacity: { value: 1.0 },
            transparent: true
          },
          colors: {
            secondary: 0xBC00BF,
            primary: 0xFF5F5F
          },
          lights: {
            primary: 0.8,
            secondary: 0.5
          },
          numTouches: 0,
          touchScreenTime: 0,
          touchesToComplete: 3,
          timeToIncrease: 800,
          percent: 0.333,
          complete: false,
          info: 'city'
        },
        {
          attributes: {
            displacement: { type: 'v3', value: [] },
            customColor: { type: 'c', value: [] }
          },
          uniforms: {
            amplitude: { value: 0.0 },
            opacity: { value: 1.0 },
            transparent: true
          },
          colors: {
            secondary: 0xAB6FFF,
            primary: 0x8EFFFC
          },
          lights: {
            primary: 0.8,
            secondary: 0.5
          },
          numTouches: 0,
          touchScreenTime: 0,
          touchesToComplete: 3,
          timeToIncrease: 800,
          percent: 0.334,
          complete: false,
          info: 'city'
        }
      ],
      fastCopy: 'You think fast.',
      slowCopy: 'You’re very concise.',
      currentStage: 0,
      progressPct: 0,
      stageProgressPct: 0,
      objContainer: new THREE.Object3D(),
      timelines: [],
      tweenTargets: [],
      lastAngle: 0,
      iconActive: true,
      geoWatchID: 0
    }
  },
  watch: {
    currentStage (newStage, oldStage) {
      this.delta = 0
      this.currentStage = ThreeMath.clamp( newStage, 0, this.stages.length - 1 )
      this.updateStage()
    }
  },
  computed: {
    progressSize () {
      return THREE.Math.clamp( this.fullWidth * 0.6, 200, 360 )
    },
    stageProgress () {
      return Number(this.stages[this.currentStage].numTouches / this.stages[this.currentStage].touchesToComplete)
    },
    stageRingColor () {
      let rgb = new THREE.Color(this.stages[this.currentStage].colors.primary)
      return `rgba(${rgb.r * 255}, ${rgb.g * 255}, ${rgb.b * 255}, 0.75)`
    },
    fastOrSlow () {
      let totalStageTouches = 0
      this.stages.forEach(stage => { totalStageTouches += stage.touchesToComplete })
      return (this.touches.length >= totalStageTouches) ? reportTxtStyles.default : reportTxtStyles.extended
    },
    currentInfo () {
      let info = this.stages[this.currentStage].info
      let modelStr = 'Neat, you\'re on '
      let locationStr = '<br>Beautiful this time of year!'
      switch (this.currentStage) {
        case 0:
          modelStr += indefiniteArticle(this.device[info]) +
            '<br><span class="green">' +
            this.device[info] +
            '</span>'
          return modelStr
        case 1: return '<span class="orange">' + this.device[info] + '...</span>' + locationStr
        case 2: return this.resultCopy
      }
    },
    indefiniteModel () {
      return indefiniteArticle(this.device['model'])
    },
    resultPreCopy () {
      return (this.fastOrSlow === reportTxtStyles.default) ? 'Amazing.' : 'Fantastic.'
    },
    resultCopy () {
      return (this.fastOrSlow === reportTxtStyles.default) ? this.fastCopy : this.slowCopy
    },
    ...mapState({
      device: state => state.device,
      didViewReport: state => state.report.didView
    }),
    ...mapState('demo2', {
      iconComplete: state => state.complete
    })
  },
  methods: {
    setupScene () {
      this.camera.far = 800
      this.camera.position.set(0, 15, 40)
      this.camera.lookAt(0, 0, 0)

      this.scene.background = new THREE.Color(0xfafafa)

      this.controls = new THREE.TrackballControls(this.camera)
      this.controls.noZoom = true
      this.controls.noPan = true
      this.controls.dynamicDampingFactor = 0.1
      this.controls.rotateSpeed = 1
      this.controls.enabled = false

      this.scene.add(this.objContainer)

      this.lights.primary = new THREE.PointLight(0xffffff, this.stages[0].lights.primary, 0)
      this.lights.primary.position.set(15, 10, 0)
      this.camera.add(this.lights.primary)
      this.tweenTargets.push(this.lights.primary)

      // Starfields
      let starfieldGeo = new THREE.BufferGeometry()
      let starPos = []
      let starColors = []
      let starSizes = []
      let starAlphas = []
      let color = new THREE.Color(0x333333)
      let radius = this.camera.far / 2
      for (let i = 0; i < this.maxStars; i++) {
        starPos.push( ( Math.random() * 2 - 1 ) * radius )
        starPos.push( ( Math.random() * 2 - 1 ) * radius )
        starPos.push( ( Math.random() * 2 - 1 ) * radius )
        starColors.push( color.r, color.g, color.b )
        starAlphas.push( ThreeMath.randFloat(0.6, 0.85) )
        starSizes.push( 15 )
      }

      starfieldGeo.addAttribute( 'position', new THREE.Float32BufferAttribute( starPos, 3 ) )
      starfieldGeo.addAttribute( 'nPosition', new THREE.Float32BufferAttribute( starPos, 3 ) )
      starfieldGeo.addAttribute( 'color', new THREE.Float32BufferAttribute( starColors, 3 ) )
      starfieldGeo.addAttribute( 'alpha', new THREE.Float32BufferAttribute( starAlphas, 1 ) )
      starfieldGeo.addAttribute( 'size', new THREE.Float32BufferAttribute( starSizes, 1 ) )

      let pointImg = new THREE.TextureLoader().load(require('@/assets/img/demos/particle2.png'))
      let starUniforms = {
        opacity: { value: 0.66 },
        texture: { value: pointImg },
        progress: { value: 0.85 },
        sizeFactor: { value: 200.0 }
      }

      let starMaterial = new THREE.ShaderMaterial( {
        uniforms: starUniforms,
        vertexShader: starfieldShader.vertex,
        fragmentShader: starfieldShader.fragment,
        depthTest: true,
        alphaTest: 0.2,
        transparent: true,
        vertexColors: true
      })

      let mainStarfield = new THREE.Points(starfieldGeo, starMaterial)
      this.scene.add(mainStarfield)

      let geomRadius = 8
      // Setup geometries
      this.stages[0].geom = new THREE.TetrahedronGeometry(geomRadius, 0)
      this.stages[1].geom = new THREE.OctahedronGeometry(geomRadius, 0)
      this.stages[2].geom = new THREE.IcosahedronGeometry(geomRadius, 0)

      this.stages.forEach(stage => {
        let explodeMod = new THREE.ExplodeModifier()
        explodeMod.modify( stage.geom )

        let numFaces = stage.geom.faces.length
        let geometry = new THREE.BufferGeometry().fromGeometry( stage.geom )
        let colors = new Float32Array( numFaces * 3 * 3 )
        let displacement = new Float32Array( numFaces * 3 * 3 )
        let color = new THREE.Color(stage.colors.primary)

        for ( let f = 0; f < numFaces; f++ ) {
          let index = 9 * f
          let faceNormal = stage.geom.faces[f].normal

          for ( let i = 0; i < 3; i++ ) {
            let faceScalar = faceNormal.addScalar(THREE.Math.randFloat( 0.5, 1.5 ))
            colors[ index + ( 3 * i ) ] = color.r
            colors[ index + ( 3 * i ) + 1 ] = color.g
            colors[ index + ( 3 * i ) + 2 ] = color.b

            displacement[ index + ( 3 * i ) ] = faceScalar.x
            displacement[ index + ( 3 * i ) + 1 ] = faceScalar.y
            displacement[ index + ( 3 * i ) + 2 ] = faceScalar.z
          }
        }

        geometry.addAttribute( 'customColor', new THREE.BufferAttribute( colors, 3 ) )
        geometry.addAttribute( 'displacement', new THREE.BufferAttribute( displacement, 3 ) )

        stage.uniforms.diffuse = {type: 'c', value: new THREE.Color(stage.colors.secondary)}
        stage.uniforms = THREE.UniformsUtils.merge([THREE.UniformsLib['lights'], stage.uniforms])

        stage.material = new THREE.ShaderMaterial({
          uniforms: stage.uniforms,
          vertexShader: displacementShader.vertex,
          fragmentShader: displacementShader.fragment,
          flatShading: true,
          transparent: true,
          depthTest: true,
          lights: true,
          side: THREE.DoubleSide
        })
        stage.mesh = new THREE.Mesh(geometry, stage.material)
        stage.mesh.visible = false
        this.objContainer.add(stage.mesh)

        // Starfields
        let starfieldGeo = new THREE.BufferGeometry()
        let starPos = []
        let starTargets = []
        let starColors = []
        let starSizes = []
        let starAlphas = []
        let scolor = new THREE.Color(stage.colors.secondary)
        for (let i = 0; i < this.maxStars / 5; i++) {
          let cosTheta = 2 * Math.random() - 1
          let sinTheta = Math.sqrt( 1 - cosTheta * cosTheta )
          let phi = Math.random() * ( Math.PI * 2 )
          starPos.push( 0 )
          starPos.push( 0 )
          starPos.push( 0 )
          let randX = ( radius / 3 ) * sinTheta * Math.cos( phi )
          let randY = ( radius / 3 ) * sinTheta * Math.sin( phi )
          let randZ = ( radius / 3 ) * cosTheta
          starTargets.push( THREE.Math.randFloatSpread(randX) )
          starTargets.push( THREE.Math.randFloatSpread(randY) )
          starTargets.push( THREE.Math.randFloatSpread(randZ) )
          starColors.push( scolor.r, scolor.g, scolor.b )
          starAlphas.push( ThreeMath.randFloat(0.6, 0.9) )
          starSizes.push( 5 )
        }

        starfieldGeo.addAttribute( 'position', new THREE.Float32BufferAttribute( starPos, 3 ) )
        starfieldGeo.addAttribute( 'nPosition', new THREE.Float32BufferAttribute( starTargets, 3 ) )
        starfieldGeo.addAttribute( 'color', new THREE.Float32BufferAttribute( starColors, 3 ) )
        starfieldGeo.addAttribute( 'alpha', new THREE.Float32BufferAttribute( starAlphas, 1 ) )
        starfieldGeo.addAttribute( 'size', new THREE.Float32BufferAttribute( starSizes, 1 ) )

        let starUniforms = {
          opacity: { value: 0.66 },
          texture: { value: pointImg },
          progress: { value: 0.0 },
          sizeFactor: { value: 150.0 }
        }

        stage.starUniforms = starUniforms

        let starMaterial = new THREE.ShaderMaterial( {
          uniforms: stage.starUniforms,
          vertexShader: starfieldShader.vertex,
          fragmentShader: starfieldShader.fragment,
          depthTest: true,
          alphaTest: 0.4,
          transparent: true,
          vertexColors: true
        })

        let stageStarfield = new THREE.Points(starfieldGeo, starMaterial)
        stage.starfield = stageStarfield
        this.tweenTargets.push(stage.mesh.scale)
        this.tweenTargets.push(stage.starUniforms.progress)
      })

      this.onResize()
    },
    onTouchStart (e) {
      this.didIncreaseByTime = false
      this.touchActive = true
      this.lastTouch.startX = e.changedTouches[0].pageX
      this.lastTouch.startY = e.changedTouches[0].pageY
      this.lastTouch.startTime = new Date().getTime()
    },
    onTouchMove (e) {
      e.preventDefault()
    },
    onTouchEnd (e) {
      this.touchActive = false
      this.stages[this.currentStage].touchScreenTime = 0
      if ( !this.didIncreaseByTime ) {
        this.lastTouch.endX = e.changedTouches[0].pageX
        this.lastTouch.endY = e.changedTouches[0].pageY
        this.lastTouch.endTime = new Date().getTime()
        this.touches.push(this.lastTouch)

        this.checkDistance()
        this.checkStage()
      }
    },
    checkDistance () {
      let diffX = Math.abs( this.lastTouch.startX - this.lastTouch.endX )
      let diffY = Math.abs( this.lastTouch.startY - this.lastTouch.endY )
      let maxDiff = Math.max( diffX, diffY )
      if ( maxDiff > 100 ) {
        let val = THREE.Math.smootherstep( maxDiff, 50, 800 )
        let amp = this.currentStage === this.stages.length - 1 ? 1 : 2
        let exTween = this.explodeTween( amp )
        let exProg = { value: 0 }
        TweenMax.to( exProg, 0.33, {
          value: val,
          repeat: 1,
          yoyo: true,
          onUpdate: ( t ) => {
            let decay = THREE.Math.clamp( THREE.Math.radToDeg( this.lastAngle * 2 ), 0, 100 )
            let ss = THREE.Math.smoothstep( decay, 0.5, 50 )
            let prog = t.target.reverse ? ( t.target.value * ss ) + t.target.value : t.target.value
            exTween.progress(prog)
          },
          onUpdateParams: ['{self}'],
          onRepeat: ( t ) => {
            t.target.reverse = true
          },
          onRepeatParams: ['{self}']
        } )
        this.tweenTargets.push(exProg)
      }
    },
    checkStage () {
      let curStage = this.stages[this.currentStage]

      if ( this.touchActive ) {
        if ( curStage.touchScreenTime >= curStage.timeToIncrease ) {
          this.didIncreaseByTime = true
          curStage.touchScreenTime = 0
          if ( curStage.numTouches < curStage.touchesToComplete - 1 ) {
            curStage.numTouches++
            this.scaleMesh()
          }
        }
      } else {
        let lastTouchTime = this.lastTouch.endTime - this.lastTouch.startTime
        if ( lastTouchTime >= this.tapThreshold ) {
          curStage.touchScreenTime = 0
          curStage.numTouches++
          this.scaleMesh()
        }
      }

      if ( curStage.numTouches >= curStage.touchesToComplete && !this.didIncreaseByTime ) {
        curStage.complete = true
        this.currentStage < this.stages.length - 1
        ? this.swapGeometry()
        : this.explodeGeometry()
      }
    },
    explodeTween ( maxAmplitude = 2, timeScale = 1 ) {
      let targets = []
      this.stages.forEach( stage => {
        targets.push(stage.uniforms.amplitude)
      } )
      let explodeTimeline = new TimelineMax( { paused: true } )
      explodeTimeline.add(
        TweenMax.fromTo( targets, 1, { value: 0.0 }, { value: maxAmplitude } )
      ).timeScale( timeScale )
      this.timelines.push(explodeTimeline)
      return explodeTimeline
    },
    scaleMesh () {
      let curStage = this.stages[this.currentStage]
      let mesh = curStage.mesh
      let scalePct = (0.8 * this.stageProgress) + 0.8
      TweenMax.to(mesh.scale, 2, {
        x: scalePct,
        y: scalePct,
        z: scalePct,
        ease: Elastic.easeOut.config(0.5)
      })
      TweenMax.to('#progress', 1, {
        scale: ThreeMath.lerp(0.75, 1.2, this.stageProgress),
        ease: Elastic.easeOut.config(0.5)
      })
      TweenMax.to('#stageProgress', 1, {
        delay: 0.1,
        scale: ThreeMath.lerp(0.75, 1.2, this.stageProgress),
        ease: Elastic.easeOut.config(0.5)
      })
      TweenMax.to(this, 1, {
        stageProgressPct: this.stageProgress,
        ease: Power2.easeOut,
        onUpdate: this.$refs.stageProg.update,
        onComplete: this.$refs.stageProg.update
      })
    },
    updateStage () {
      this.controls.enabled = true
      this.controls.dynamicDampingFactor = 0.1
      let curStage = this.stages[this.currentStage]
      TweenMax.to(this.lights.primary, 1, {
        intensity: curStage.lights.primary,
        ease: Power2.easeOut
      })
      TweenMax.set( curStage.mesh, { visible: true } )
      TweenMax.set( curStage.mesh.scale, { x: 1.333, y: 1.333, z: 1.333 } )
      TweenMax.to('#progress', 1, {
        scale: ThreeMath.lerp(0.8, 1.25, this.stageProgress),
        ease: Elastic.easeOut.config(0.5)
      })
      TweenMax.to('#stageProgress', 1, {
        delay: 0.1,
        scale: ThreeMath.lerp(0.8, 1.25, this.stageProgress),
        ease: Elastic.easeOut.config(0.5)
      })
      let delay = 0.25
      let diffX = Math.abs( this.lastTouch.startX - this.lastTouch.endX )
      let diffY = Math.abs( this.lastTouch.startY - this.lastTouch.endY )
      let maxDiff = Math.max( diffX, diffY )
      if ( maxDiff < 50 ) delay = 0
      TweenMax.to( curStage.mesh.scale, 1, {
        delay: delay,
        x: 0.8,
        y: 0.8,
        z: 0.8,
        ease: Elastic.easeOut.config(0.5)
      } )
      this.enableTouchListeners()
    },
    tessellateMore ( stage, geomRadius ) {
      let tessMod = new THREE.TessellateModifier( 3 )
      let explodeMod = new THREE.ExplodeModifier()
      tessMod.modify( stage.geom )
      tessMod.modify( stage.geom )
      tessMod.modify( stage.geom )
      tessMod.modify( stage.geom )
      tessMod.modify( stage.geom )
      explodeMod.modify( stage.geom )

      let numFaces = stage.geom.faces.length
      let geometry = new THREE.BufferGeometry().fromGeometry( stage.geom )
      let colors = new Float32Array( numFaces * 3 * 3 )
      let displacement = new Float32Array( numFaces * 3 * 3 )
      let color = new THREE.Color(stage.colors.primary)

      for ( let f = 0; f < numFaces; f++ ) {
        let index = 9 * f
        let cosTheta = 2 * Math.random() - 1
        let sinTheta = Math.sqrt( 1 - cosTheta * cosTheta )
        let phi = Math.random() * ( Math.PI * 2 )
        let randSX = THREE.Math.randFloatSpread( geomRadius * sinTheta * Math.cos( phi ) )
        let randSY = THREE.Math.randFloatSpread( geomRadius * sinTheta * Math.sin( phi ) )
        let randSZ = THREE.Math.randFloatSpread( geomRadius * cosTheta )

        for ( let i = 0; i < 3; i++ ) {
          colors[ index + ( 3 * i ) ] = color.r
          colors[ index + ( 3 * i ) + 1 ] = color.g
          colors[ index + ( 3 * i ) + 2 ] = color.b
          displacement[ index + ( 3 * i ) ] = randSX
          displacement[ index + ( 3 * i ) + 1 ] = randSY
          displacement[ index + ( 3 * i ) + 2 ] = randSZ
        }
      }

      geometry.addAttribute( 'customColor', new THREE.BufferAttribute( colors, 3 ) )
      geometry.addAttribute( 'displacement', new THREE.BufferAttribute( displacement, 3 ) )
      stage.mesh.geometry.dispose()
      stage.mesh.geometry = geometry
    },
    swapGeometry () {
      let curStage = this.stages[this.currentStage]
      let mesh = curStage.mesh
      this.touchActive = false
      this.controls.enabled = false
      this.disableTouchListeners()
      this.explodeStarfield()
      TweenMax.to(this, 1, {
        progressPct: '+=' + curStage.percent,
        ease: Power2.easeOut,
        onUpdate: this.$refs.prog.update
      })

      TweenMax.to(this, 1, {
        delay: 0.1,
        stageProgressPct: 0,
        ease: Power2.easeOut,
        onUpdate: this.$refs.stageProg.update,
        onComplete: this.$refs.stageProg.update
      })
      mesh.visible = false
      this.showData()
    },
    explodeGeometry () {
      let curStage = this.stages[this.currentStage]
      let mesh = curStage.mesh
      this.touchActive = false
      this.controls.enabled = false
      this.disableTouchListeners()
      this.explodeStarfield()
      this.tessellateMore( curStage, 8 )

      let explodeTl = new TimelineMax( { onComplete: () => {
        TweenMax.set( mesh.scale, { x: 0.001, y: 0.001, z: 0.001 } )
        TweenMax.to( '.info', 0.75, {
          bottom: '45%',
          ease: Power2.easeInOut
        } )
        this.showData()
      } } )

      explodeTl.add( TweenMax.to( this.controls, 1, {
        dynamicDampingFactor: 0.2,
        ease: Power2.easeOut
      } ) )

      explodeTl.add( TweenMax.to( curStage.uniforms.amplitude, 1, {
        value: 8.0,
        ease: Power2.easeOut
      } ), 0 )

      explodeTl.add( TweenMax.to( curStage.uniforms.opacity, 1, {
        value: 0,
        ease: Power3.easeInOut
      } ), 0.5 )

      explodeTl.add( TweenMax.to( curStage.mesh.scale, 0.75, {
        x: 0.001,
        y: 0.001,
        z: 0.001,
        ease: Power3.easeInOut
      } ), 0.5 )

      explodeTl.add( TweenMax.to( this, 0.75, {
        progressPct: '+=' + curStage.percent,
        ease: Power2.easeOut,
        onUpdate: this.$refs.prog.update
      } ), 0.5 )

      explodeTl.add( TweenMax.to( this, 0.75, {
        stageProgressPct: 0,
        ease: Power2.easeOut,
        onUpdate: this.$refs.stageProg.update,
        onComplete: this.$refs.stageProg.update
      } ), 0.6 )

      explodeTl.add( TweenMax.staggerTo( ['#stageProgress', '#progress'], 0.75, {
        scale: 0,
        ease: Elastic.easeIn.config( 0.5 )
      }, 0.075, this.showData), '-=0.75' )

      explodeTl.add( TweenMax.to( '#glview', 1, {
        opacity: 0.25,
        ease: Power2.easeOut
      } ), '-=1')

      explodeTl.add( TweenMax.to(
        this.$refs.hint, 0.75, {
          opacity: 0,
          y: -100,
          ease: Power2.easeIn
        } ), 0
      )

      this.timelines.push( explodeTl )
    },
    explodeStarfield () {
      let sf = this.stages[this.currentStage].starfield
      let sfUni = this.stages[this.currentStage].starUniforms
      this.scene.add( sf )
      TweenMax.to( sfUni.progress, 2, {
        delay: 0.5,
        value: 1.0,
        ease: Power3.easeOut
      } )
    },
    showData () {
      if ( this.currentStage < this.stages.length ) {
        TweenMax.to( '.info' + this.currentStage, 1, {
          delay: 0.5,
          height: 30,
          opacity: 1,
          ease: Elastic.easeOut.config( 0.25 ),
          onComplete: ( cs ) => {
            if ( cs === this.stages.length - 1 ) {
              this.showInterstitial()
            }
          },
          onCompleteParams: [this.currentStage]
        } )
        TweenMax.to( `.info${this.currentStage} span:nth-child(2)`, 1, {
          delay: 1.5,
          opacity: 1,
          ease: Power2.easeOut
        })

        if ( this.currentStage < this.stages.length - 1 ) this.currentStage++
      }
    },
    update (dt) {
      this.controls.update()
      this.lastAngle = this.controls.lastAngle.toFixed(2)

      if ( this.shouldUpdate ) {
        let curStage = this.stages[this.currentStage]
        if ( this.touchActive ) {
          curStage.touchScreenTime += dt * 1000
          this.checkStage()
        }
      }

      this.camera.updateProjectionMatrix()
    },
    onProgressComplete () {
      this.touchActive = false
      this.controls.enabled = false
      this.disableTouchListeners()
      this.isComplete = true
      this.$store.dispatch( 'setDemoComplete', this.idx )
      this.$store.dispatch( 'setReportStyle', this.fastOrSlow )
    },
    initScroller () {
      navigator.geolocation.clearWatch( this.geoWatchID )
      this.enableTouchListeners()
      this.$parent.showControls()
      this.animate()
      this.iconActive = false

      if ( this.skipIntro ) {
        this.introActive = false
        this.controls.enabled = true
      } else {
        this.$nextTick( () => {
          if ( this.$refs.intro ) {
            TweenMax.to( this.$refs.intro, 0.6, {
              opacity: 0,
              ease: Power2.easeInOut,
              onComplete: () => {
                this.introActive = false
                this.controls.enabled = true
              }
            } )
          }
        } )
      }

      TweenMax.to( '#progress', 1, {
        delay: 0.75,
        scale: 0.75,
        ease: Elastic.easeOut.config( 0.5 )
      } )
      TweenMax.to( '#stageProgress', 1, {
        delay: 0.85,
        scale: 0.75,
        ease: Elastic.easeOut.config( 0.5 )
      } )
      TweenMax.set( this.stages[this.currentStage].mesh, { visible: true } )
      TweenMax.set( this.stages[this.currentStage].mesh.scale, { x: 0.001, y: 0.001, z: 0.001 } )
      TweenMax.to( this.stages[this.currentStage].mesh.scale, 1, {
        delay: 0.5,
        x: 0.8,
        y: 0.8,
        z: 0.8,
        ease: Elastic.easeOut.config( 0.5 )
      } )
      this.tweenTargets.push(this.$refs.intro)
      this.tweenTargets.push(this.stages[this.currentStage].mesh.scale)
      this.showHint()
    },
    showIntro () {
      this.$store.dispatch( 'enableDemo', this.idx )
      this.$store.dispatch( 'activateDemo', this.idx )
      TweenMax.staggerTo( '.intro-item', 1, {
        delay: 0.5,
        opacity: 1,
        y: 0,
        rotationX: 0,
        rotationY: 0,
        ease: Power2.easeOut
      }, 0.15)
      TweenMax.delayedCall( this.introDelay, this.promptGeo )
      this.tweenTargets.push( this )
      this.tweenTargets.push( 'intro-item' )
      this.tweenTargets.push( this.promptGeo )
      this.tweenTargets.push( '.info0' )
      this.tweenTargets.push( '.info1' )
      this.tweenTargets.push( '.info2' )
      this.tweenTargets.push( '#progress' )
      this.tweenTargets.push( '#stageProgress' )
      this.tweenTargets.push( this.$refs.hint )
    },
    promptGeo () {
      if ( navigator.geolocation ) {
        this.geoWatchID = navigator.geolocation.watchPosition( this.onGeoSuccess, this.initScroller, { timeout: 6000 } )
      } else {
        this.initScroller()
      }
    },
    onGeoSuccess (position) {
      navigator.geolocation.clearWatch( this.geoWatchID )
      this.initScroller()
      let coords = [ position.coords.latitude, position.coords.longitude ].join( ',' )
      this.$root.$children[0].spy.getLocation( coords ).then( loc => {
        this.$store.dispatch( 'setCity', loc )
      } ).catch( this.initScroller )
    },
    showHint () {
      if ( this.$refs.hint ) {
        TweenMax.to( this.$refs.hint, 1, {
          delay: 2,
          opacity: 1,
          y: 0,
          ease: Elastic.easeOut.config( 0.25 )
        } )
      }
    },
    showInterstitial () {
      TweenMax.set( '.selection', { y: 50, opacity: 0 } )
      TweenMax.set( '.select-item', { y: 20, opacity: 0, rotationX: 20, rotationY: 50 } )
      this.$nextTick(() => {
        let interTl = new TimelineMax( { delay: 1.5 } )

        interTl.add( TweenMax.staggerTo(
          '.info-txt', 0.75, {
            opacity: 0,
            ease: Power2.easeIn
          }, 0.1 ), 0
        )

        interTl.add( TweenMax.to( '.selection', 1, {
          y: 0,
          opacity: 1,
          ease: Power2.easeOut
        } ) )

        interTl.add( TweenMax.staggerTo( '.select-item', 1, {
          y: 0,
          opacity: 1,
          rotationX: 0,
          rotationY: 0,
          ease: Power2.easeOut
        }, 0.15 ), '-=0.5' )

        interTl.add( TweenMax.delayedCall( 3, this.showOutro ) )

        this.timelines.push( interTl )
      })
    },
    showOutro () {
      this.outroActive = true
      this.$nextTick(() => {
        TweenMax.set( '.outro', { opacity: 0 } )
        TweenMax.set( '.outro-item', { opacity: 0, y: 25, rotationX: 20, rotationY: 50 } )
        let outroTl = new TimelineMax()
        outroTl.add( TweenMax.staggerTo( '.select-item', 0.75, {
          y: -20,
          opacity: 0,
          ease: Power2.easeIn
        }, 0.1 ) )
        outroTl.add( TweenMax.to( '.outro', 0.75, {
          opacity: 1,
          ease: Power2.easeOut
        } ) )
        outroTl.add( TweenMax.staggerTo( '.outro-item', 1, {
          y: 0,
          opacity: 1,
          rotationX: 0,
          rotationY: 0,
          ease: Power2.easeInOut
        }, 0.15 ) )
        this.timelines.push( outroTl )
      })
    },
    enableTouchListeners () {
      this.$nextTick( () => {
        if ( this.$refs.glview ) {
          this.$refs.glview.addEventListener('touchstart', this.onTouchStart)
          this.$refs.glview.addEventListener('touchmove', this.onTouchMove)
          this.$refs.glview.addEventListener('touchend', this.onTouchEnd)
        }
      } )
    },
    disableTouchListeners () {
      this.$refs.glview.removeEventListener('touchstart', this.onTouchStart)
      this.$refs.glview.removeEventListener('touchmove', this.onTouchMove)
      this.$refs.glview.removeEventListener('touchend', this.onTouchEnd)
    },
    killAllTimelines () {
      this.timelines.forEach( tl => tl.kill() )
    }
  },
  created () {
    window.addEventListener('resize', this.onResize)
  },
  mounted () {
    this.fullWidth = (window.screen) ? screen.availWidth : window.innerWidth
    this.fullHeight = (window.screen) ? screen.availHeight : window.innerHeight
    this.$nextTick(() => {
      this.init('glview')
      this.setupScene()
      TweenMax.set( this.$refs.hint, { opacity: 0, y: -100 } )
      TweenMax.set( '.intro-item', { opacity: 0, y: 20, rotationX: 20, rotationY: 50 } )
      TweenMax.set( '.icon.intro-item', { opacity: 0, y: 20, rotationX: 0, rotationY: 0 } )
      TweenMax.set( '.info-txt', { height: 0, opacity: 0 } )
      TweenMax.set( ['#progress', '#stageProgress'], { scale: 0 } )
      if (!this.skipIntro) {
        this.showIntro()
      } else {
        this.initScroller()
      }
    })
  },
  beforeDestroy () {
    navigator.geolocation.clearWatch( this.geoWatchID )
    this.$store.dispatch('deactivateDemo', this.idx)
    TweenMax.killTweensOf(this.tweenTargets)
    this.killAllTimelines()
    this.disableTouchListeners()
    this.controls.enabled = false
    window.removeEventListener('resize', this.onResize)
    cancelAnimationFrame(this.rafID)
  }
}
</script>

<style lang="scss">
body {
  overscroll-behavior: none;
}
</style>

<style lang="scss" scoped>
@import "~@/assets/scss/global/components/demo-common";

.demo {

  #progress,
  #stageProgress {
    position          : absolute;
    top               : 0;
    right             : 0;
    bottom            : 0;
    left              : 0;
    margin            : auto;
    transform-origin  : 50% 50%;
    pointer-events    : none;
  }

  .info {
    position          : absolute;
    bottom            : 100px;
    left              : 0;
    right             : 0;
    height            : 180px;
    max-width         : 420px;
    margin            : auto;
    padding           : 0 25px;
    color             : $black;
    pointer-events    : none;


    .info-txt {
      display           : flex;
      flex-direction    : row;
      font-size         : 1.1rem;
      transform-origin  : bottom left;
      font-style        : italic;

      span:first-child {
        flex-grow : 1;
      }

      span:last-child {
        opacity : 0;
        font-weight: 600;
      }

      &.info2 {
        font-size   : 1.3rem;
        font-weight : bold;
      }
    }

    & /deep/ .green {
      color : #66b5a3;
    }

    & /deep/ .orange {
      color : #FF5F5F;
    }

    & /deep/ .purple {
      color : #AB6FFF;
    }
  }

  .selection {
    border: 10px solid #AB6FFF;
    border-image: linear-gradient(135deg, #8EFFFC 0%, #AB6FFF 100%) 10;
  }

}
</style>



// WEBPACK FOOTER //
// src/components/demos/DemoScroll.vue